import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useGameStore, GameState } from '../store/useGameStore';
import { Settings, Clock } from 'lucide-react';
import { useChatStore, getChatId } from '../store/useChatStore';
import { toast } from 'sonner';
import VerboLogo from '../components/VerboLogo';
import { GameSummary } from '../components/GameSummary';
import { RobotAvatar } from '../components/RobotAvatar';
import { getVirtualRivalName, isVirtualRivalPlayerId } from '../constants/virtualRivals';
import { formatDuration } from '../utils/time';
import { useAchievementStore } from '../store/useAchievementStore';
import { clsx } from 'clsx';

function GameCard({
  game,
  currentUserId,
  navigate
}: {
  game: GameState;
  currentUserId: string;
  navigate: any;
}) {
  const fetchUserProfile = useAuthStore((state) => state.fetchUserProfile);
  const chats = useChatStore((state) => state.chats);

  const [opponentName, setOpponentName] = useState<string>('');

  const opponentId = game.players.find((id) => id !== currentUserId);
  const isMyTurn = game.currentTurn === currentUserId;

  const chatId = opponentId ? getChatId(currentUserId, opponentId) : null;
  const chat = chatId ? chats[chatId] : null;
  const unreadCount = chat?.unreadCounts?.[currentUserId] || 0;

  useEffect(() => {
    if (!opponentId) {
      setOpponentName('...');
      return;
    }

    if (isVirtualRivalPlayerId(opponentId)) {
      setOpponentName(getVirtualRivalName(game.botDifficulty));
      return;
    }

    let isMounted = true;

    fetchUserProfile(opponentId).then((profile) => {
      if (isMounted) {
        setOpponentName(profile?.nickname || '...');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [opponentId, fetchUserProfile, game.botDifficulty]);

  const myScore = game.playerDetails[currentUserId]?.score || 0;
  const oppId = game.players.find((id) => id !== currentUserId);
  const oppScore = oppId ? game.playerDetails[oppId]?.score || 0 : 0;

  const lastMoveDate = new Date(game.updatedAt);
  const now = new Date();
  const diffInMs = now.getTime() - lastMoveDate.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);

  const timeText = `Hace ${formatDuration(diffInMs / 1000)}`;
  const isUrgent = isMyTurn && diffInDays >= 1;

  return (
    <button
      onClick={() => navigate(`/game/${game.id}`)}
      className={clsx(
        'w-full bg-card-bg border border-grid-line/30 rounded-xl py-2 px-3 flex flex-col gap-1.5 transition-all active:scale-[0.98] text-left shadow-sm',
        isMyTurn ? 'border-primary-accent/40 bg-primary-accent/5' : 'opacity-80'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full border-2 border-grid-line/20 overflow-hidden bg-app-bg shrink-0 flex items-center justify-center">
            {isVirtualRivalPlayerId(opponentId) ? (
              <RobotAvatar robotId={game.botDifficulty || 'nilo'} size={16} className="w-full h-full" />
            ) : (
              <img
                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${opponentId || 'default'}`}
                alt="Opponent"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            )}
          </div>

          <div className="min-w-0">
            <div className="font-black text-text-primary truncate text-xs uppercase tracking-tight">
              {isVirtualRivalPlayerId(opponentId)
                ? getVirtualRivalName(game.botDifficulty)
                : opponentName}
            </div>
          </div>
        </div>

        <span className="text-[9px] text-text-muted font-bold flex items-center gap-1 shrink-0">
          <Clock className="w-2.5 h-2.5" />
          {isMyTurn ? timeText : `Jugaste ${timeText.toLowerCase()}`}
        </span>
      </div>

      <div className="flex items-start gap-4 relative">
        <div className="flex flex-col shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-black text-text-primary leading-none">{myScore}</span>
            {isMyTurn && (
              <div className="w-1.5 h-1.5 rounded-full bg-primary-accent shadow-[0_0_8px_rgba(var(--primary-accent-rgb),0.5)]" />
            )}
          </div>
          <span className="text-lg font-black text-text-primary leading-none opacity-80">{oppScore}</span>
        </div>

        <div className="flex-1">
          <GameSummary
            level={2}
            language={game.language}
            dictionaryMode={game.dictionaryMode}
            gameMode={game.mode}
            opponentType={isVirtualRivalPlayerId(opponentId) ? 'virtual' : 'player'}
            botDifficulty={game.botDifficulty}
            opponentName={opponentName}
            showOpponentName={false}
            coachMode={game.coachMode}
            coachId={game.coachId}
            exchangeMode={game.exchangeMode}
            swapsAllowed={game.swapsAllowed}
            timeMode={game.timeLimit === null ? 'unlimited' : 'timed'}
            turnTimeSeconds={game.timeLimit}
          />
        </div>
      </div>
    </button>
  );
}

export default function Home() {
  const user = useAuthStore((state) => state.user);

  const activeGames = useGameStore((state) => state.activeGames);
  const fetchActiveGames = useGameStore((state) => state.fetchActiveGames);

  const subscribeToChats = useChatStore((state) => state.subscribeToChats);

  const evaluateAchievements = useAchievementStore((state) => state.evaluateAchievements);

  const navigate = useNavigate();

  const [logoTaps, setLogoTaps] = useState(0);
  const [longPressTimer, setLongPressTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    fetchActiveGames(user.uid);

    const unsubscribeChats = subscribeToChats(user.uid);

    return () => {
      if (typeof unsubscribeChats === 'function') {
        unsubscribeChats();
      }
    };
  }, [user?.uid, fetchActiveGames, subscribeToChats]);

  useEffect(() => {
    if (!user?.uid) return;
    evaluateAchievements(user.uid);
  }, [user?.uid, evaluateAchievements]);

  const handleLogoClick = () => {
    const newTaps = logoTaps + 1;

    if (newTaps >= 5) {
      setLogoTaps(0);
      navigate('/developer');
      toast.info('Modo desarrollador activado');
    } else {
      setLogoTaps(newTaps);
      setTimeout(() => setLogoTaps(0), 2000);
    }
  };

  const handleAvatarPressStart = () => {
    const timer = setTimeout(() => {
      navigate('/developer');
      toast.info('Modo desarrollador activado');
    }, 3000);

    setLongPressTimer(timer);
  };

  const handleAvatarPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  if (!user) return null;

  return (
    <div className="w-full h-screen flex flex-col space-y-0 relative overflow-hidden bg-app-bg">
      <header className="h-[70px] shrink-0 flex items-center justify-between px-4 bg-card-bg border-b border-grid-line/30 shadow-sm z-10 relative">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Abrir perfil del jugador"
            onClick={() => navigate('/profile')}
            onMouseDown={handleAvatarPressStart}
            onMouseUp={handleAvatarPressEnd}
            onMouseLeave={handleAvatarPressEnd}
            onTouchStart={handleAvatarPressStart}
            onTouchEnd={handleAvatarPressEnd}
            onTouchCancel={handleAvatarPressEnd}
            className="group flex min-h-11 items-center gap-2 rounded-full p-1 pr-3 cursor-pointer hover:bg-app-bg active:scale-95 transition-all"
          >
            <div className="relative">
              <img
                src={user.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.nickname}`}
                alt="Avatar"
                className="w-9 h-9 rounded-full border-2 border-primary-accent shadow-sm object-cover transition-transform group-hover:scale-105"
                referrerPolicy="no-referrer"
              />
              <span className="absolute -right-0.5 -bottom-0.5 w-3 h-3 rounded-full bg-card-bg border border-grid-line flex items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-accent" />
              </span>
            </div>
            <div className="text-left hidden xs:block">
              <div className="text-sm font-black text-text-primary leading-none">{user.nickname}</div>
              <div className="text-[10px] font-bold text-primary-accent uppercase tracking-wider">{user.league}</div>
            </div>
          </button>
        </div>

        <div
          className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center cursor-pointer"
          onClick={handleLogoClick}
        >
          <VerboLogo size={24} />
        </div>

        <div className="flex items-center gap-1">
          <div className="flex flex-col items-center">
            <button
              onClick={() => navigate('/settings')}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-app-bg rounded-full transition-all active:scale-90"
            >
              <Settings className="w-5 h-5" />
            </button>
            <span className="text-[8px] font-black text-text-muted mt-[-6px] pointer-events-none uppercase tracking-tighter">
              v1.0.0
            </span>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
        <div className="space-y-4">
          <button
            onClick={() => navigate('/setup')}
            className="w-full bg-card-bg hover:bg-grid-line/5 text-text-primary border-2 border-grid-line/30 py-5 rounded-2xl font-black flex flex-col items-center justify-center transition-all active:scale-95 shadow-md"
          >
            <div className="flex items-center gap-2 text-xl tracking-tight">
              INICIAR PARTIDA
            </div>
            <span className="text-[10px] font-bold text-text-muted uppercase tracking-widest mt-0.5">
              Juega a tu estilo
            </span>
          </button>
        </div>

        <div className="space-y-8 pb-8">
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-accent animate-pulse" />
              <h3 className="font-black text-text-primary uppercase tracking-[0.2em] text-xs">TU TURNO</h3>
              <div className="flex-1 h-[1px] bg-grid-line/20" />
            </div>

            {activeGames?.filter((g) => g.currentTurn === user.uid).length === 0 ? (
              <p className="text-center py-4 text-text-muted text-xs font-medium italic opacity-60">
                No tienes partidas pendientes
              </p>
            ) : (
              <div className="space-y-3">
                {activeGames
                  ?.filter((g) => g.currentTurn === user.uid)
                  .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
                  .map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      currentUserId={user.uid}
                      navigate={navigate}
                    />
                  ))}
              </div>
            )}
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-text-muted opacity-40" />
              <h3 className="font-black text-text-muted uppercase tracking-[0.2em] text-xs">EN ESPERA</h3>
              <div className="flex-1 h-[1px] bg-grid-line/20" />
            </div>

            {activeGames?.filter((g) => g.currentTurn !== user.uid).length === 0 ? (
              <p className="text-center py-4 text-text-muted text-xs font-medium italic opacity-60">
                No hay partidas en espera
              </p>
            ) : (
              <div className="space-y-3">
                {activeGames
                  ?.filter((g) => g.currentTurn !== user.uid)
                  .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                  .map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      currentUserId={user.uid}
                      navigate={navigate}
                    />
                  ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
